import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import type { PageContent } from '../../services/api';
import api from '../../services/api';

import styles from './ContentSelector.module.css';

/**
 * ContentSelector Component
 *
 * Displays a list of all available pages that can be edited.
 * Acts as a landing page before navigating to specific ContentEditor routes.
 *
 * Features:
 * - Shows all pages with their titles and types
 * - Quick navigation to edit specific pages
 * - Visual indicators for page types
 * - Loading and error states
 */

const ContentSelector = (): JSX.Element => {
  const [pages, setPages] = useState<ReadonlyArray<PageContent>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all menu items to get page slugs
      const menuItems = await api.menu.getAll();

      // Fetch content for each page
      const pagePromises = menuItems.map((item) =>
        api.content.getBySlug(item.slug).catch((err) => {
          console.warn(`Failed to load page ${item.slug}:`, err);
          return null;
        })
      );

      const loadedPages = await Promise.all(pagePromises);
      const validPages = loadedPages.filter((page): page is PageContent => page !== null);

      setPages(validPages);
    } catch (err) {
      const error = err as { readonly message?: string };
      console.error('Failed to load pages:', err);
      setError(error.message ?? 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  // Get icon for page type
  const getPageTypeIcon = (type: string): string => {
    switch (type) {
      case 'text-cloud':
        return '📝';
      case 'icon-ring':
        return '🔗';
      case 'image-gallery':
        return '🖼️';
      case 'shop-gallery':
        return '🛒';
      default:
        return '📄';
    }
  };

  // Get human-readable page type name
  const getPageTypeName = (type: string): string => {
    switch (type) {
      case 'text-cloud':
        return 'Text Cloud';
      case 'icon-ring':
        return 'Icon Ring';
      case 'image-gallery':
        return 'Image Gallery';
      case 'shop-gallery':
        return 'Shop Gallery';
      default:
        return type;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles.contentSelector}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading pages...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={styles.contentSelector}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2>Error Loading Pages</h2>
          <p className={styles.errorMessage}>{error}</p>
          <div className={styles.errorActions}>
            <button onClick={loadPages} className={styles.retryButton}>
              🔄 Retry
            </button>
            <Link to="/admin/dashboard" className={styles.backButton}>
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentSelector}>
      <div className={styles.header}>
        <h1 className={styles.title}>Edit Content</h1>
        <p className={styles.subtitle}>Select a page to edit its content</p>
      </div>

      {pages.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📄</div>
          <h2>No Pages Found</h2>
          <p className={styles.emptyMessage}>
            Create menu items first to add pages that can be edited.
          </p>
          <Link to="/admin/menu" className={styles.createButton}>
            → Go to Menu Editor
          </Link>
        </div>
      ) : (
        <div className={styles.pageGrid}>
          {pages.map((page) => (
            <Link
              key={page._id || page.slug}
              to={`/admin/content/${page.slug}`}
              className={styles.pageCard}
            >
              <div className={styles.pageIcon}>{getPageTypeIcon(page.type)}</div>
              <div className={styles.pageContent}>
                <h3 className={styles.pageTitle}>{page.metadata?.title ?? page.slug}</h3>
                <p className={styles.pageType}>{getPageTypeName(page.type)}</p>
                {page.metadata?.description ? (
                  <p className={styles.pageDescription}>{page.metadata.description}</p>
                ) : null}
              </div>
              <div className={styles.pageAction}>
                <span className={styles.editIcon}>✏️</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentSelector;
